import { MouseEvent, useCallback } from 'react';

import { calendarEvent } from '@/utils/calendarEvent';
import { generateIcsFile } from '@/utils/generateIcsFile';
import {
    getCatalogDescription,
    getCatalogSellerName,
    getCatalogStartTime,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getRegistrationNotify } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { isUiMobile } from '@/redux/modules/browser';
import { toggleCatalogNotifications } from '@/redux/modules/catalog/registration/catalogRegistration.actions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useTrackAddToCalendarPressedAnalytics } from '@/utils/amplitude/analytics/registration/trackAddToCalendarPressed.analytics';

type Props = {
    catalogId: number;
};

export const useAddToCalendarClicked = ({ catalogId }: Props) => {
    const dispatch = useAppDispatch();
    const catalogSaleStartTs = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const description = useAppSelector((state) => getCatalogDescription(state, catalogId));
    const sellerName = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const notify = useAppSelector((state) => getRegistrationNotify(state, catalogId));
    const isMobile = useAppSelector(isUiMobile);
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const trackAddToCalendarPressed = useTrackAddToCalendarPressedAnalytics();

    return useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();

            // Create the calendar event object
            const calendarEventObject = calendarEvent({
                catalogId,
                description,
                saleStartTs: catalogSaleStartTs,
                sellerName,
                title,
            });

            // Generate ICS file content
            const icsContent = generateIcsFile(calendarEventObject);

            // Track analytics
            trackAddToCalendarPressed({ calendarType: 'ics' });

            if (isMobile) {
                // For mobile, open the ICS content in a new window
                // Mobile devices will handle opening their native calendar app
                const dataUrl = `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;
                window.open(dataUrl, '_blank');
            } else {
                // For desktop, download the ICS file by programmatically clicking a temporary link element
                const filename = 'auction-event.ics';
                const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });

                // Create a temporary link, trigger download, and clean up immediately
                // The link is never visible to users as it's removed synchronously
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Clean up the blob URL
                window.URL.revokeObjectURL(link.href);
            }

            // Toggle catalog notifications
            dispatch(toggleCatalogNotifications({ catalogId, notify: !notify }));
        },
        [
            catalogId,
            catalogSaleStartTs,
            description,
            dispatch,
            isMobile,
            notify,
            sellerName,
            title,
            trackAddToCalendarPressed,
        ]
    );
};
